import React, { useState, useRef, useEffect } from "react";
import { Col, InputNumber, Row, Slider, Space } from "antd";
import { canvasPreview, useDebounceEffect } from "./canvasUtils";
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  convertToPixelCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import Compressor from "compressorjs";
import { BorderOutlined } from "@ant-design/icons";

const CropImage = ({
  imgSrc,
  onClickImgHandler,
  toggle,
  file,
  crop,
  setCrop,
  aspectRatio,
  setAspectRatio,
}) => {
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [completedCrop, setCompletedCrop] = useState();
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);

  function centerAspectCrop(mediaWidth, mediaHeight, aspectRatio) {
    return centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
        },
        aspectRatio,
        mediaWidth,
        mediaHeight
      ),
      mediaWidth,
      mediaHeight
    );
  }

  function onImageLoad(e) {
    if (aspectRatio) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspectRatio));
    }
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          zoom,
          rotation
        );
      }
    },
    100,
    [completedCrop, zoom, rotation]
  );

  const getResult = async (e) => {
    e.preventDefault();
    toggle();
    const image = imgRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!image || !previewCanvas || !completedCrop) {
      throw new Error("Crop canvas does not exist");
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = 1;
    const offscreen = new OffscreenCanvas(
      completedCrop.width * scaleX * pixelRatio,
      completedCrop.height * scaleY * pixelRatio
    );
    const ctx = offscreen.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    if (!ctx) {
      throw new Error("No 2d context");
    }

    ctx.drawImage(
      previewCanvas,
      0,
      0,
      previewCanvas.width,
      previewCanvas.height,
      0,
      0,
      offscreen.width,
      offscreen.height
    );

    const blob = await offscreen.convertToBlob({
      type: "image/jpg",
      quality: 0.7,
    });

    const _file = new File([blob], file?.name, {
      type: file?.type,
      lastModified: Date.now(),
    });

    console.error("Before Resized file size:", _file.size / 1024, "KB");

    new Compressor(_file, {
      quality: 0.7,
      success(result) {
        const compressedFile = new File([result], file.name, {
          type: file?.type,
          lastModified: Date.now(),
        });
        console.error(
          "After Resized file size:",
          compressedFile.size / 1024,
          "KB"
        );
        onClickImgHandler(compressedFile);
      },
    });
  };

  function handleToggleAspectClick() {
    if (aspectRatio) {
      setAspectRatio(undefined);
    } else {
      setAspectRatio(1 / 1);
      if (imgRef.current) {
        const { width, height } = imgRef.current;
        const newCrop = centerAspectCrop(width, height, 1 / 1);
        setCrop(newCrop);
        setCompletedCrop(convertToPixelCrop(newCrop, width, height));
      }
    }
  }

  return (
    <div className="container-fluid p-0">
      <div
        className="row"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 999999,
          background: "white",
        }}
      >
        <div className="col-12">
          <div className="row px-3">
            <div className="d-flex flex-fill">
              <div className="flex-fill">
                <span className="d-block mb-2 ">Zoom</span>
                <Slider
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  aria-labelledby="Zoom"
                  onChange={(value) => setZoom(value)}
                />
              </div>
              <div className="flex-fill">
                <span className="d-block mb-2">Rotation</span>
                <Slider
                  value={rotation}
                  min={0}
                  max={360}
                  step={1}
                  aria-labelledby="Rotation"
                  onChange={(rotation) => setRotation(rotation)}
                />
              </div>
            </div>
            <div className="">
              <button
                className="btn btn-sm btn-primary"
                onClick={handleToggleAspectClick}
              >
                {aspectRatio ? "Free crop" : "Fix Shape"}
              </button>
            </div>
          </div>
        </div>
        <div className="col-12">File Name : {file?.name}</div>
      </div>
      <div className="row">
        <div
          className="col-12 position-relative"
          style={{
            justifyContent: "center",
            display: "flex",
          }}
        >
          <ReactCrop
            key={aspectRatio}
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => {
              setCompletedCrop(c);
            }}
            aspect={aspectRatio}
            width={50}
            height={50}
          >
            <img
              ref={imgRef}
              alt="Crop me"
              src={imgSrc}
              onLoad={onImageLoad}
              style={{
                transform: `scale(${zoom}) rotate(${rotation}deg)`,
                objectFit: "contain",
                height: "100%",
                width: "100%",
              }}
            />
          </ReactCrop>
        </div>
        <div
          style={{
            position: "sticky",
            zIndex: 999999,
            background: "white",
            bottom: 0,
          }}
          className="col-12 mt-4 py-2"
        >
          <div className="d-flex gap-1 align-items-center justify-content-center">
            <button className="btn btn-primary" onClick={getResult}>
              Submit
            </button>
            <button className="btn btn-secondary" onClick={() => toggle()}>
              Cancel
            </button>
          </div>
        </div>
        <div className="col-12 mt-3">
          <div className="row">
            <div className="col-12 d-flex justify-content-center">
              <canvas
                ref={previewCanvasRef}
                style={{
                  border: "1px solid black",
                  objectFit: "contain",
                  width: completedCrop?.width,
                  height: completedCrop?.height,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CropImage;
